"use client";

import React from "react";
import { CircleAlert as AlertCircle, BookOpen, FileSpreadsheet, ShieldCheck, Scale } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const methodologyPoints = [{
  id: 1,
  title: "Synthesized scenario modeling",
  description: "Each review reconstructs complex organizational challenges using cross-industry telemetry and peer-reviewed operational benchmarks."
}, {
  id: 2,
  title: "Empirical diagnostic frameworks",
  description: "Evaluations follow repeatable analytical stages: diagnostic audit, variable isolation, structural intervention, and measurable impact assessment."
}, {
  id: 3,
  title: "Non-proprietary anonymization",
  description: "Corporate metrics and private identifiers are sanitized into generalized operational models to safeguard confidentiality while preserving strategic rigor."
}];
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="intro" data-nav="light" className="relative w-full bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead rule motif */}
        <div className="w-full mb-10 sm:mb-12">
          <div className="h-[1px] w-full bg-foreground" />
          <div className="h-[0.5px] w-full bg-border mt-[2px]" />
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Main Editorial Header (7 Cols) */}
          <div className="lg:col-span-7 flex flex-col space-y-6">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2.5">
              <Badge variant="outline" className="bg-card text-muted-foreground border-border font-mono text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-sm">
                Analytical Frameworks
              </Badge>
              <span className="text-muted-foreground text-xs font-mono">•</span>
              <span className="text-muted-foreground font-sans text-xs font-medium tracking-normal">
                Volume IV: Operational Diagnostic Models
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.18] text-foreground">
              Applied methodology <span className="italic font-normal">in practice.</span>
            </motion.h1>

            <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Examine how structured research frameworks resolve complex operational bottlenecks across enterprise
              workflows. The following case studies serve as methodological demonstrations of problem-solving
              models developed by MinfoMedia analysts.
            </motion.p>

            {/* Structured Methodology Pillars */}
            <motion.div variants={itemVariants} className="pt-4 border-t border-border/70 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                {methodologyPoints.map((point, i) => <div key={point.id} data-index={i} className="p-3.5 bg-card border border-border rounded-md flex flex-col justify-between transition-colors hover:border-primary/40">
                    <div className="space-y-1.5">
                      <span className="font-mono text-[11px] text-muted-foreground block">
                        {"0" + (i + 1)}
                      </span>
                      <h3 className="font-sans font-semibold text-xs text-foreground tracking-tight leading-snug">
                        {point.title}
                      </h3>
                      <p className="font-sans text-[11px] text-muted-foreground leading-normal">
                        {point.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
          </div>

          {/* Sample / Demo Editorial Disclaimer Banner (5 Cols) */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="relative bg-card border border-border rounded-md p-6 sm:p-7 shadow-xs">
              {/* Top Accent Indicator */}
              <div className="flex items-center justify-between gap-3 pb-4 mb-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-secondary text-secondary-foreground rounded-sm">
                    <Scale className="size-4 text-foreground" />
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground block">
                      Publication Protocol
                    </span>
                    <span className="font-sans font-semibold text-xs text-foreground">
                      Methodological Disclosure
                    </span>
                  </div>
                </div>
                <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary border-transparent text-[11px] font-mono rounded-sm px-2 py-0.5">
                  Sample Models
                </Badge>
              </div>

              {/* Notice Body */}
              <div className="space-y-3.5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="size-4 text-foreground shrink-0 mt-0.5" />
                  <p className="font-sans text-xs text-foreground leading-relaxed">
                    <strong className="font-semibold">Illustrative research notice:</strong> The records featured in
                    this archive are synthesized analytical demonstrations designed to showcase strategic assessment
                    techniques, hypothesis testing, and quantitative problem-solving frameworks.
                  </p>
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <ShieldCheck className="size-4 text-muted-foreground shrink-0 mt-0.5" />
                  <p className="font-sans text-xs text-muted-foreground leading-relaxed">
                    These studies illustrate analytical methodologies rather than verified client proprietary
                    outcomes. For peer-reviewed primary datasets and market surveys, consult our dedicated research
                    repository.
                  </p>
                </div>
              </div>

              {/* Action Links */}
              <div className="mt-6 pt-5 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button asChild variant="default" size="sm" className="rounded-sm font-sans text-xs px-4 py-2.5 h-auto justify-center bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ResearchReportsPage">
                    <BookOpen className="size-3.5 mr-1.5" />
                    Browse verified reports
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="rounded-sm font-sans text-xs px-4 py-2.5 h-auto justify-center border-border text-foreground hover:bg-secondary/60">
                  <Link to="/ResourcesPage">
                    <FileSpreadsheet className="size-3.5 mr-1.5" />
                    Resource library
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}